import { motion } from 'framer-motion';
import { Scale, Shield, FileText, Users, Eye, AlertTriangle, DollarSign } from 'lucide-react';
import SectionHeading from '../components/SectionHeading';

const boardMembers = [
  { name: 'Board Member 1', role: 'President', placeholder: true },
  { name: 'Board Member 2', role: 'Vice President', placeholder: true },
  { name: 'Board Member 3', role: 'Secretary', placeholder: true },
  { name: 'Board Member 4', role: 'Treasurer', placeholder: true },
];

const policies = [
  {
    icon: Scale,
    title: 'Conflict of Interest Policy',
    description: 'All board members and volunteers must disclose any potential conflicts of interest. Decisions involving conflicts are made transparently with recusal of affected parties.',
  },
  {
    icon: Shield,
    title: 'Anti-Fraud Policy',
    description: 'We maintain strict controls to prevent fraud, including separation of duties, regular audits, and clear reporting channels for suspected misconduct.',
  },
  {
    icon: Eye,
    title: 'Whistleblower Protection',
    description: 'Individuals who report concerns in good faith are protected from retaliation. Anonymous reporting channels are available.',
  },
  {
    icon: FileText,
    title: 'Document Retention',
    description: 'Financial records, meeting minutes, and program documentation are retained according to CRA requirements and best practices.',
  },
];

const GovernancePage = () => {
  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="py-16 lg:py-24 hero-gradient islamic-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Accountability
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground font-['Outfit'] leading-tight mb-6">
              Governance & <span className="text-gradient">Transparency</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We are committed to the highest standards of transparency and accountability in all our operations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Board of Directors */}
      <section className="py-24 lg:py-32" data-testid="board-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="Leadership"
            title="Board of Directors"
            description="Our volunteer board provides strategic direction and oversight for the organization."
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {boardMembers.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-card border border-border text-center"
                data-testid={`board-member-${index}`}
              >
                <div className="w-20 h-20 rounded-full bg-accent flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="font-semibold font-['Outfit'] text-foreground">{member.name}</h3>
                <p className="text-sm text-muted-foreground">{member.role}</p>
                {member.placeholder && (
                  <span className="inline-block mt-2 px-2 py-1 rounded text-xs bg-secondary/10 text-secondary">
                    Coming Soon
                  </span>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Policies */}
      <section className="py-24 lg:py-32 bg-card islamic-pattern" data-testid="policies-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="Policies"
            title="Our Governance Framework"
            description="Strong policies ensure proper stewardship of donated funds and ethical operations."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {policies.map((policy, index) => (
              <motion.div
                key={policy.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-background border border-border"
                data-testid={`policy-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-primary/10">
                    <policy.icon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold font-['Outfit'] text-foreground mb-2">{policy.title}</h3>
                    <p className="text-sm text-muted-foreground">{policy.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Financial Transparency */}
      <section className="py-24 lg:py-32" data-testid="financial-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="Financial Reporting"
            title="Financial Transparency"
            description="We believe donors have the right to know how their contributions are used."
          />

          <div className="max-w-3xl mx-auto space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 rounded-2xl bg-card border border-border"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-secondary/10">
                  <DollarSign className="h-6 w-6 text-secondary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold font-['Outfit'] text-foreground mb-2">Annual Financial Reports</h3>
                  <p className="text-muted-foreground mb-4">
                    Annual financial statements will be made available once our organization completes its first fiscal year. 
                    These reports will detail income, expenses, and program spending.
                  </p>
                  <span className="inline-block px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm">
                    Coming Soon - First Report Expected 2025
                  </span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 rounded-2xl bg-card border border-border"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-primary/10">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold font-['Outfit'] text-foreground mb-2">CRA Registration Status</h3>
                  <p className="text-muted-foreground mb-4">
                    True North Muslim Community has applied for registered charity status with the Canada Revenue Agency. 
                    Once approved, we will issue official tax receipts for donations.
                  </p>
                  <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                    Application In Progress
                  </span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 rounded-2xl bg-secondary/10 border border-secondary/20"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Request Information</h4>
                  <p className="text-sm text-muted-foreground">
                    If you would like more information about our governance, finances, or operations, 
                    please contact us at <a href="mailto:support@truenorthmuslimcommunity.ca" className="text-primary hover:underline">support@truenorthmuslimcommunity.ca</a>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="py-24 lg:py-32 bg-card islamic-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <SectionHeading
              badge="Our Promise"
              title="Commitment to Excellence"
              description="We hold ourselves to the highest standards of governance and accountability."
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="p-6 rounded-2xl bg-background border border-border">
                <div className="text-3xl font-bold text-primary font-['Outfit'] mb-2">100%</div>
                <p className="text-sm text-muted-foreground">Volunteer Board</p>
              </div>
              <div className="p-6 rounded-2xl bg-background border border-border">
                <div className="text-3xl font-bold text-primary font-['Outfit'] mb-2">Full</div>
                <p className="text-sm text-muted-foreground">Financial Disclosure</p>
              </div>
              <div className="p-6 rounded-2xl bg-background border border-border">
                <div className="text-3xl font-bold text-primary font-['Outfit'] mb-2">CRA</div>
                <p className="text-sm text-muted-foreground">Compliant Operations</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GovernancePage;
